'use client'
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'

import { Picture, Text, Button, Container, BrandStrip } from '../../components/index.js'

import { withLayout } from '../../shared/Styles.js'

import { cn, getLocalizedPath } from '../../helpers/utils.js'

import type {
  MobilePrimaryNavigationData,
  MobileSecondaryNavigationData,
  MobileNavigationProps,
  DesktopNavigationProps,
  HeaderNavigationProps,
} from './types.js'
import NavProvider, { useNavContext } from './NavProvider.js'

import { HeaderNavigationMenu, HeaderNavigationBlockType, HeaderNavigationActionBlockType } from './types.js'

import jump from 'jump.js'

import { useGSAP } from '@gsap/react'
import { gsap } from 'gsap'

import { WithSidebar, WithSupports, WithMulticol, WithGroupCategory, WithResource } from './withCategory.js'

import { Menu, Close, User, LeftArrow, DownArrow, Polygon } from './icons/index.js'

import NavigationSearch from '../NavigationSearch/index.js'
import { ResourceSidebarDropdown } from './ResourceSidebarDropdown.js'
import { MobileResourceSidebarMenu } from './MobileResourceSidebarMenu.js'
import { SidebarDropdown } from './SidebarDropdown.js'
import { useScrollLock } from './useScrollLock.js'
import { MulticolDropdown } from './MulticolDropdown.js'
import { SupportsDropdown } from './SupportsDropdown.js'
import { MobileSidebarMenu } from './MobileSidebarMenu.js'
import { MobileSupportMenu } from './MobileSupportMenu.js'
import { MobileMulticolMenu } from './MobileMulticolMenu.js'
import { MenuItem } from './MobileMenuComponents.js'
import { useAiuiContext } from '../AiuiProvider/index.js'
import throttle from '../../shared/throttle.js'

const HeaderNavigation = forwardRef<HTMLDivElement, HeaderNavigationProps>((props, ref) => {
  const {
    data: { header } = {},
    buildProps,
    event,
    profile,
    theme = 'light',
    isTop = false,
    searchResult,
    onSearch,
    isSearching,
    keywords,
    onPrimaryNavClick,
    onSeriesProductClick,
    onSidebarNavClick,
    headerId,
    cartCount,
    menuData,
    ...rest
  } = props

  const [searchOpen, setSearchOpen] = useState(false)
  const [headerHidden, setHeaderHidden] = useState(false)
  const [changeHeaderBackground, setChangeHeaderBackground] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const searchRef = useRef<HTMLDivElement>(null)

  const headerRef = useRef<HTMLDivElement>(null)

  const resizeWindow = () => {
    const offsetWidth = document?.querySelector('body')?.offsetWidth || 0
    setIsMobile(offsetWidth <= 1440)
  }

  useEffect(() => {
    resizeWindow()
    window.addEventListener('resize', resizeWindow)
    return () => {
      window.removeEventListener('resize', resizeWindow)
    }
  }, [])

  useImperativeHandle(ref, () => headerRef.current as HTMLDivElement)

  useEffect(() => {
    if (headerRef.current && isTop) {
      jump(headerRef.current, { duration: 0, offset: headerRef.current?.getBoundingClientRect()?.bottom || 0 })
    }
  }, [isTop])

  useEffect(() => {
    // 如果 isTop 为 true，不需要滚动监听和动效
    if (isTop) {
      setHeaderHidden(false)
      setChangeHeaderBackground(false)
      return
    }

    let lastScrollY = 0
    let upwardDistance = 0
    let downwardDistance = 0

    const getScrollTop = () => {
      return document?.documentElement?.scrollTop || document?.body?.scrollTop || 0
    }

    // 初始化状态
    const initialScrollTop = getScrollTop()
    lastScrollY = initialScrollTop
    setHeaderHidden(initialScrollTop > 300)
    setChangeHeaderBackground(initialScrollTop > 30)

    const handleScroll = throttle(() => {
      const currentScrollY = getScrollTop()
      const delta = currentScrollY - lastScrollY

      // 更新背景状态
      setChangeHeaderBackground(currentScrollY > 30)

      if (delta > 0) {
        // 向下滚动
        upwardDistance = 0 // 重置向上累计
        downwardDistance += delta

        if (downwardDistance >= 300 && currentScrollY > 300) {
          setHeaderHidden(true)
        }
      } else if (delta < 0) {
        // 向上滚动
        downwardDistance = 0 // 重置向下累计
        upwardDistance += Math.abs(delta)

        if (upwardDistance >= 300) {
          setHeaderHidden(false)
          upwardDistance = 0
        }
      }

      lastScrollY = currentScrollY
    }, 100)

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [isTop])

  useEffect(() => {
    if (event) {
      event.search = () => setSearchOpen(true)
    }
  }, [event])

  useGSAP(() => {
    if (searchRef?.current && searchOpen) {
      gsap.fromTo(
        searchRef.current,
        {
          height: 0,
        },
        {
          height: 'auto',
          duration: 0.3,
        }
      )
    }
  }, [searchOpen])

  useScrollLock(searchOpen)

  const searchPage = useMemo(() => {
    return (
      header?.bar?.actions?.find((item: any) => item?.blockType === HeaderNavigationActionBlockType.Search)
        ?.searchBar?.[0] || {}
    )
  }, [header])

  return (
    <NavProvider
      buildProps={buildProps}
      profile={profile}
      isMobile={isMobile}
      event={event}
      payloadData={header}
      onSidebarNavClick={onSidebarNavClick}
      onSeriesProductClick={onSeriesProductClick}
      cartCount={cartCount}
    >
      <header
        {...rest}
        id={headerId || 'header'}
        data-ui-component-id="HeaderNavigation"
        className={
          isTop
            ? 'relative z-[100] bg-white'
            : cn('sticky top-0 z-[100] transition-transform duration-500 ease-in-out', {
                ['translate-y-[-100%]']: headerHidden,
              })
        }
        // className="relative z-[100] bg-white"
        ref={headerRef}
      >
        <div
          className={cn(
            'hover:bg-white hover:text-black',
            theme === 'light' ? 'text-black' : 'text-white',
            {
              ['!bg-white transition-all duration-500 ease-in-out']: !isTop && changeHeaderBackground,
            },
            {
              ['hover:!text-black']: theme === 'dark',
              ['!text-black']: !isTop && changeHeaderBackground,
            }
          )}
          onClick={() => !isTop && setChangeHeaderBackground(true)}
        >
          <DesktopNavigation
            data={header}
            className="desktop:block hidden !bg-white"
            theme={theme}
            onNavItemClick={() => !isTop && setChangeHeaderBackground(true)}
            onPrimaryNavClick={onPrimaryNavClick}
          />
          <MobileNavigation
            menuData={menuData}
            data={header}
            className="desktop:hidden block !bg-white"
            onPrimaryNavClick={onPrimaryNavClick}
          />
          {searchOpen && (
            <div
              className="absolute left-0 top-0 z-[60] flex w-full flex-col bg-black/70"
              style={{ height: `calc(100dvh - ${headerRef?.current?.getBoundingClientRect()?.top}px)` }}
              role="dialog"
              aria-modal="true"
              aria-label="Search"
            >
              <div ref={searchRef} className={cn('overflow-y-auto', {})}>
                <NavigationSearch
                  data={searchPage}
                  keywords={keywords}
                  isSearching={isSearching}
                  searchResult={searchResult}
                  onSearch={(value: string) => {
                    onSearch?.(value)
                  }}
                  onClose={() => {
                    onSearch?.()
                    setSearchOpen(false)
                  }}
                />
              </div>
              <div
                className="flex-1 bg-transparent"
                onClick={() => setSearchOpen(false)}
                role="button"
                tabIndex={0}
                aria-label="Close search"
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ' || e.key === 'Escape') {
                    e.preventDefault()
                    setSearchOpen(false)
                  }
                }}
              />
            </div>
          )}
        </div>
      </header>
    </NavProvider>
  )
})

const DesktopNavigation = forwardRef<HTMLDivElement, DesktopNavigationProps>((props, ref) => {
  const { data, onNavItemClick, className, theme, onPrimaryNavClick } = props
  const { event, profile } = useNavContext()
  const [downdownHover, setDowndownHover] = useState(false)
  const { locale = 'us' } = useAiuiContext()

  const groupCategories = useMemo(() => {
    return WithGroupCategory(data?.categories?.filter((item: any) => item?.pcShow)) as any[][]
  }, [data])

  const [categoriesItem, setCategoriesItem] = useState<any>(null)
  const [navStatusArray, setNavStatusArray] = useState<{ index: number; groupIndex: number; open: boolean }[][]>([])
  const profileRef = useRef<HTMLButtonElement>(null)
  const [userProfileOpen, setUserProfileOpen] = useState(false)
  const headerRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const navItemRefs = useRef<HTMLDivElement[][]>(
    groupCategories.map((categories: any) => Array(categories?.length || 0).fill(null))
  )

  useEffect(() => {
    if (groupCategories?.length) {
      setNavStatusArray(
        groupCategories?.map((groupItem: any, groupIndex: number) => {
          return groupItem?.map((_: any, index: number) => ({ groupIndex, index, open: false }))
        })
      )
    }
  }, [groupCategories])

  const currentNavItem = useMemo(() => {
    let currentNavItem: { index: number; groupIndex: number; open: boolean } | null = null
    for (const groupItem of navStatusArray) {
      for (const item of groupItem) {
        if (item.open) {
          currentNavItem = item
          break
        }
      }
      if (currentNavItem) break
    }
    return currentNavItem
  }, [navStatusArray])

  useScrollLock(!!currentNavItem?.open || userProfileOpen)

  const handleNavItemClick = (e: any, groupIndex: number, index: number) => {
    setUserProfileOpen(false)
    // if (categoriesItem?.components?.[0]?.blockType === HeaderNavigationBlockType.Links) {
    const currentCategoriesItem = groupCategories[groupIndex][index]
    if (currentCategoriesItem?.components?.[0]?.blockType === HeaderNavigationBlockType.Links) {
      // link模块特殊处理
      // categoriesItem?.components?.[0]?.url && window.open(categoriesItem?.components?.[0]?.url)
      currentCategoriesItem?.components?.[0]?.url && window.open(currentCategoriesItem?.components?.[0]?.url)
    } else {
      const categories = groupCategories?.flat() || []
      const position = categories?.findIndex(item => item?.id === groupCategories[groupIndex][index]?.id)
      e.stopPropagation()
      onNavItemClick?.()
      onPrimaryNavClick?.(categories[position], position)
      // setCategoriesItem(groupCategories[groupIndex][index])
      setCategoriesItem(currentCategoriesItem)
      setNavStatusArray(prev =>
        prev.map(groupItem =>
          groupItem.map(item =>
            item.groupIndex === groupIndex && item.index === index
              ? { ...item, open: !item.open }
              : { ...item, open: false }
          )
        )
      )
    }
  }

  const handleNavItemKeyDown = (e: React.KeyboardEvent, groupIndex: number, index: number) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleNavItemClick(e, groupIndex, index)
    } else if (e.key === 'Escape' && currentNavItem?.groupIndex === groupIndex && currentNavItem?.index === index) {
      e.preventDefault()
      handleNavMenuClose()
    }
  }

  const handleNavMenuClose = () => {
    setNavStatusArray(prev => prev.map(groupItem => groupItem.map(item => ({ ...item, open: false }))))
  }

  const currentBlockTypeCategories = useMemo(() => {
    if (categoriesItem) return categoriesItem?.components?.[0]?.blockType
  }, [categoriesItem])

  const SidebarDropdownComp = WithSidebar(SidebarDropdown, categoriesItem)
  const ResourceSidebarDropdownComp = WithResource(ResourceSidebarDropdown, categoriesItem)
  const MulticolDropdownComp = WithMulticol(MulticolDropdown, categoriesItem)
  const SupportsDropdownComp = WithSupports(SupportsDropdown, {
    categoriesItem,
    currentNavItemRef: navItemRefs.current?.[currentNavItem?.groupIndex || 0]?.[currentNavItem?.index || 0],
  })

  const DropdownComp = useMemo(() => {
    switch (currentBlockTypeCategories) {
      case HeaderNavigationBlockType.Sidebar:
        return <SidebarDropdownComp />
      case HeaderNavigationBlockType.Resource:
        return <ResourceSidebarDropdownComp />
      case HeaderNavigationBlockType.Supports:
        return <SupportsDropdownComp />
      case HeaderNavigationBlockType.Multicol:
        return <MulticolDropdownComp />
      default:
        return null
    }
  }, [currentBlockTypeCategories, categoriesItem])

  const actions = useMemo(() => {
    return data?.bar?.actions?.filter((item: any) => item?.pcShow)
  }, [data])

  const profileAction = useMemo(() => {
    return actions?.find((item: any) => item?.blockType === HeaderNavigationActionBlockType.Profile)
  }, [actions])

  const handleProfileClick = useCallback(() => {
    setUserProfileOpen(prev => !prev)
  }, [])

  useEffect(() => {
    if (profileRef?.current) {
      const profileRefElement = profileRef.current as HTMLButtonElement
      profileRefElement.addEventListener('click', handleProfileClick)

      return () => {
        profileRefElement.removeEventListener('click', handleProfileClick)
      }
    }
  }, [handleProfileClick])

  useGSAP(() => {
    if (currentNavItem?.open) {
      gsap.fromTo(
        dropdownRef?.current,
        {
          height: 0,
        },
        {
          height: 'auto',
        }
      )
    }
  }, [currentNavItem?.open])

  return (
    <Container className={cn('relative h-[96px]', className)}>
      <div ref={headerRef} onClick={handleNavMenuClose} className="flex h-full flex-col justify-end gap-4">
        <div className="flex items-center justify-between">
          <Logo />
          <Actions ref={profileRef} actions={actions} activeStatus={userProfileOpen} />
        </div>
        <nav className="flex justify-between" role="navigation" aria-label="Primary navigation">
          {groupCategories?.map((groupItem: any, groupIndex: number) => {
            return (
              <div key={`groupCategory-${groupIndex}`} className="flex gap-3">
                {groupItem?.map((item: any, index: number) => {
                  const isExpanded = currentNavItem?.groupIndex === groupIndex && currentNavItem?.index === index
                  return (
                    <div
                      key={item.id}
                      ref={(el: HTMLDivElement) => {
                        navItemRefs.current[groupIndex][index] = el
                      }}
                      className="group"
                    >
                      <div className="relative">
                        <button
                          className="flex cursor-pointer items-center gap-1 border-0 bg-transparent pb-4"
                          onClick={e => handleNavItemClick(e, groupIndex, index)}
                          onKeyDown={e => handleNavItemKeyDown(e, groupIndex, index)}
                          aria-expanded={isExpanded}
                          aria-haspopup="true"
                          aria-label={item.text}
                        >
                          <Text html={item.text} className="text-sm font-bold leading-[1.4]" />
                          {/* <DownArrow
                            aria-hidden="true"
                            className={cn('size-4 opacity-0 transition-opacity duration-500 group-hover:opacity-100', {
                              ['rotate-180']: isExpanded,
                              ['opacity-100']: downdownHover && isExpanded,
                            })}
                          /> */}
                          <DownArrow
                            className={cn('size-4 opacity-0 transition-opacity duration-500 group-hover:opacity-100', {
                              ['rotate-180']:
                                currentNavItem?.groupIndex === groupIndex && currentNavItem?.index === index,
                              ['opacity-100']:
                                downdownHover &&
                                currentNavItem?.groupIndex === groupIndex &&
                                currentNavItem?.index === index,
                              ['invisible']:
                                !item?.components?.length ||
                                item?.components?.[0]?.blockType === HeaderNavigationBlockType.Links,
                            })}
                          />
                        </button>
                        <div
                          className={cn(
                            'absolute bottom-0 left-0 h-[2px] w-0 transition-all duration-500',
                            {
                              'w-[calc(100%-20px)]': isExpanded,
                            },
                            theme === 'dark' ? 'bg-white' : 'bg-[#080A0F]'
                          )}
                          aria-hidden="true"
                        />
                      </div>
                    </div>
                  )
                })}
              </div>
            )
          })}
        </nav>
      </div>
      <div
        role="menu"
        aria-hidden={!(currentNavItem?.open && categoriesItem)}
        className={cn(
          'absolute left-0 top-full z-[999] flex w-full flex-col overflow-hidden border-t border-b-[#E4E5E6] bg-black/70 text-black',
          {
            hidden: !(currentNavItem?.open && categoriesItem),
          }
        )}
        onMouseEnter={() => setDowndownHover(true)}
        onMouseLeave={() => setDowndownHover(false)}
        style={{ height: `calc(100dvh - ${headerRef?.current?.getBoundingClientRect()?.bottom}px)` }}
      >
        <div
          ref={dropdownRef}
          className={cn('relative z-50', {
            'overflow-hidden': currentBlockTypeCategories !== HeaderNavigationBlockType.Supports,
          })}
        >
          {DropdownComp}
        </div>
        <div
          className="flex-1 bg-transparent"
          onClick={handleNavMenuClose}
          role="button"
          tabIndex={0}
          aria-label="Close menu"
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ' || e.key === 'Escape') {
              e.preventDefault()
              handleNavMenuClose()
            }
          }}
        />
      </div>
      {userProfileOpen && (
        <div
          className="absolute left-0 top-full z-[999] flex h-dvh w-full bg-black/70"
          role="dialog"
          aria-modal="true"
          aria-label="User profile menu"
          // style={{
          //   height: `calc(100dvh - ${profileRef?.current?.getBoundingClientRect()?.bottom}px)`,
          //   top: `calc(96px)`,
          // }}
        >
          <div
            className="rounded-box absolute w-[272px] overflow-hidden bg-white p-4"
            style={{
              right: `calc(100% - ${profileRef?.current?.getBoundingClientRect()?.right}px)`,
              top: '-36px',
            }}
          >
            {profile?.email ? (
              <>
                <Text html={profile?.nick_name || profileAction?.welcome} className="text-sm font-bold" />
                <div className="mt-2 h-px bg-[#D9D9D9]" aria-hidden="true" />
                <nav className="mt-2" role="navigation" aria-label="Profile navigation">
                  {profileAction?.profiles?.map((item: any) => (
                    <MenuItem
                      className="py-2"
                      key={item.id}
                      label={item?.title}
                      href={getLocalizedPath(item?.url, locale)}
                    />
                  ))}
                </nav>
              </>
            ) : (
              <>
                <Polygon className="absolute -top-2 right-[46px] z-30 text-white" aria-hidden="true" />
                <Text html={profileAction?.benefits_title} className="text-sm font-bold leading-[1.4]" />
                <div className="mt-2 flex flex-col gap-1">
                  {profileAction?.benefits?.map((item: any) => (
                    <div key={item.id} className="flex items-center gap-[6px]">
                      <Picture
                        source={item.benefitIcon?.url}
                        className="size-4"
                        alt={item.benefit}
                        width={16}
                        height={16}
                      />
                      <Text html={item.benefit} className="text-sm font-bold leading-[1.4]" />
                    </div>
                  ))}
                </div>
                <div className="mt-4 flex items-center gap-2">
                  <Button variant="secondary" size="lg" onClick={() => event?.join?.()}>
                    <Text html={profileAction?.primaryButton || 'Join Now'} className="font-bold" />
                  </Button>
                  <Button variant="primary" size="lg" onClick={() => event?.login?.()}>
                    <Text html={profileAction?.secondaryButton || 'Log In'} className="font-bold" />
                  </Button>
                </div>
              </>
            )}
          </div>
          <div
            className="flex-1 bg-transparent"
            onClick={() => setUserProfileOpen(false)}
            role="button"
            tabIndex={0}
            aria-label="Close profile menu"
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ' || e.key === 'Escape') {
                e.preventDefault()
                setUserProfileOpen(false)
              }
            }}
          />
        </div>
      )}
    </Container>
  )
})

const MobileNavigation = forwardRef<HTMLDivElement, MobileNavigationProps>(
  ({ data, menuData, className, onPrimaryNavClick }, ref) => {
    const groupCategories = useMemo(() => {
      return WithGroupCategory(data?.categories?.filter((item: any) => item?.mobileShow))
    }, [data])

    const { currentMenu, setCurrentMenu, subSubCategory } = useNavContext()
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
    const [menuInnerHeight, setMenuInnerHeight] = useState(0)
    const [categoriesItem, setCategoriesItem] = useState<any>(null)

    const mobileMenuRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
      if (mobileMenuRef?.current && mobileMenuOpen) {
        const rect = mobileMenuRef?.current?.getBoundingClientRect()
        setMenuInnerHeight(window?.innerHeight - (rect?.bottom || 0))
      }
    }, [mobileMenuOpen])

    useGSAP(() => {
      gsap.fromTo(
        mobileMenuRef.current,
        {
          height: 0,
        },
        {
          height: menuInnerHeight,
          duration: 0.3,
        }
      )
    }, [menuInnerHeight])

    useScrollLock(mobileMenuOpen)

    const currentBlockTypeCategories = useMemo(() => {
      return categoriesItem?.components?.[0]?.blockType
    }, [categoriesItem])

    const MobileSupportMenuComp = WithSupports(MobileSupportMenu, {
      categoriesItem,
    })

    const MobileSidebarMenuComp = WithSidebar(MobileSidebarMenu, categoriesItem)
    const MobileResourceSidebarMenuComp = WithResource(MobileResourceSidebarMenu, categoriesItem)
    const MobileMulticolMenuComp = WithMulticol(MobileMulticolMenu, categoriesItem)

    const MobileCategoriesMenuComp = useMemo(() => {
      switch (currentBlockTypeCategories) {
        case HeaderNavigationBlockType.Sidebar:
          return <MobileSidebarMenuComp />
        case HeaderNavigationBlockType.Resource:
          return <MobileResourceSidebarMenuComp />
        case HeaderNavigationBlockType.Supports:
          return <MobileSupportMenuComp />
        case HeaderNavigationBlockType.Multicol:
          return <MobileMulticolMenuComp />
        default:
          return null
      }
    }, [currentBlockTypeCategories, categoriesItem, MobileSupportMenuComp])

    const handleSecondaryMenuClose = useCallback(() => {
      setMobileMenuOpen(false)
      setMenuInnerHeight(0)
      setCurrentMenu && setCurrentMenu(HeaderNavigationMenu.Primary)
    }, [setMobileMenuOpen, setMenuInnerHeight, setCurrentMenu])

    const menuActions = useMemo(() => {
      return data?.bar?.actions?.filter(
        (item: any) => item?.mobileShow && item?.blockType !== HeaderNavigationActionBlockType.Profile // 移动端不显示profile按钮
      )
    }, [data])

    const profileAction = useMemo(() => {
      return (
        data?.bar?.actions?.find(
          (item: any) => item?.mobileShow && item?.blockType === HeaderNavigationActionBlockType.Profile
        ) || {}
      )
    }, [data])

    const MobileCategoriesHeaderComp = useMemo(() => {
      switch (currentMenu) {
        case HeaderNavigationMenu.Primary:
          return (
            <PrimaryHeader
              actions={menuActions}
              menuOpen={mobileMenuOpen}
              onMenuOpenClose={() => {
                setMobileMenuOpen(false)
                setMenuInnerHeight(0)
              }}
              onMenuOpenClick={() => setMobileMenuOpen(true)}
            />
          )
        case HeaderNavigationMenu.Secondary:
          return (
            <SecondaryHeader
              title={categoriesItem?.text}
              onMenuOpenClose={handleSecondaryMenuClose}
              onMenuBackClick={() => setCurrentMenu?.(HeaderNavigationMenu.Primary)}
            />
          )
        case HeaderNavigationMenu.Third:
          return (
            <SecondaryHeader
              title={subSubCategory?.label}
              onMenuOpenClose={handleSecondaryMenuClose}
              onMenuBackClick={() => setCurrentMenu?.(HeaderNavigationMenu.Secondary)}
            />
          )
        default:
          return null
      }
    }, [
      mobileMenuOpen,
      currentMenu,
      setCurrentMenu,
      categoriesItem,
      menuActions,
      subSubCategory,
      handleSecondaryMenuClose,
    ])

    const handlePrimaryMenuClick = useCallback(
      (groupIndex: number, index: number) => {
        const categoriesItem = Array.isArray(groupCategories)
          ? Array.isArray(groupCategories[groupIndex])
            ? // @ts-ignore
              groupCategories[groupIndex][index]
            : {}
          : {}
        setCategoriesItem(categoriesItem)
        if (categoriesItem?.components?.[0]?.blockType === HeaderNavigationBlockType.Links) {
          // link模块特殊处理
          categoriesItem?.components?.[0]?.url && window.open(categoriesItem?.components?.[0]?.url)
        } else {
          setCurrentMenu?.(HeaderNavigationMenu.Secondary)
        }
      },
      [groupCategories, setCurrentMenu]
    )

    return (
      <Container className={cn('relative h-[52px]', className)}>
        {/* 移动端分类菜单 */}
        {MobileCategoriesHeaderComp}
        {mobileMenuOpen && (
          <nav
            ref={mobileMenuRef}
            className="absolute left-0 top-full z-[999] w-full overflow-y-auto border-t border-[#E4E5E6] bg-white text-black"
            style={{ height: menuInnerHeight }}
            role="navigation"
            aria-label="Mobile navigation menu"
          >
            {currentMenu === HeaderNavigationMenu.Primary && menuData ? (
              <div className="pt-4">
                <BrandStrip data={menuData} />
              </div>
            ) : null}
            {currentMenu === HeaderNavigationMenu.Primary ? (
              <MobilePrimaryMenu
                categories={groupCategories}
                onPrimaryMenuClick={handlePrimaryMenuClick}
                onPrimaryNavClick={onPrimaryNavClick}
                profileAction={profileAction}
              />
            ) : (
              MobileCategoriesMenuComp
            )}
          </nav>
        )}
      </Container>
    )
  }
)

/**
 * 移动端一级菜单
 * @param categories 分类
 * @param onPrimaryMenuClick 一级菜单点击
 */
const MobilePrimaryMenu = ({
  categories,
  onPrimaryMenuClick,
  profileAction,
  onPrimaryNavClick,
}: {
  categories: any
  onPrimaryMenuClick: (groupIndex: number, index: number) => void
  profileAction: any
  onPrimaryNavClick?: (category: any, position: number) => void
}) => {
  return (
    <div className="flex h-full flex-col justify-between">
      <div className="tablet:px-8 laptop:px-16 p-4">
        {categories?.map((groupItem: any, groupIndex: number) => (
          <div key={`groupCategory-${groupIndex}`} className="">
            <div className={cn('my-2 h-px w-full bg-[#E5E5E7]')} />
            {groupItem?.map((item: any, index: number) => (
              <MenuItem
                key={item.id}
                label={item.text}
                onClick={() => {
                  const flatCategories = categories?.flat() || []
                  const position = flatCategories?.findIndex(
                    (item: any) => item?.id === categories[groupIndex][index]?.id
                  )
                  onPrimaryMenuClick(groupIndex, index)
                  onPrimaryNavClick?.(item, position)
                }}
                icon={item.components?.[0]?.icon}
              />
            ))}
          </div>
        ))}
      </div>
      <ProfileComp profileAction={profileAction} />
    </div>
  )
}

/**
 * 移动端侧边栏下拉
 * @param sidebarCategoriesMetadata 侧边栏分类元数据
 * @param seriesMetadata 侧边栏系列元数据
 */
const ProfileComp = ({ profileAction }: { profileAction: any }) => {
  const [profileOpen, setProfileOpen] = useState(false)
  const { profile, event } = useNavContext()
  const { locale = 'us' } = useAiuiContext()
  return (
    <div
      className={cn('tablet:px-8 laptop:px-16 bg-[#F5F5F7] px-4 py-6', {
        'p-4': profileOpen,
      })}
    >
      <button
        className="flex w-full cursor-pointer items-center justify-between border-0 bg-transparent"
        onClick={() => setProfileOpen(!profileOpen)}
        aria-expanded={profileOpen}
        aria-label={profile?.nick_name || profileAction?.welcome}
      >
        <div className="flex items-center gap-[14px]">
          <div className="flex size-[40px] shrink-0 items-center justify-center rounded-full bg-white">
            <User aria-hidden="true" />
          </div>
          <Text html={profile?.nick_name || profileAction?.welcome} className="text-base font-bold leading-[1.4]" />
        </div>
        {!profile?.email && (
          <DownArrow aria-hidden="true" className={cn('laptop:size-4 size-5', { 'rotate-180': profileOpen })} />
        )}
      </button>
      {profile?.email && (
        <nav className="mt-4" role="navigation" aria-label="Profile menu">
          {profileAction?.profiles?.map((item: any) => (
            <MenuItem key={item.id} label={item?.title} href={getLocalizedPath(item?.url, locale)} />
          ))}
        </nav>
      )}
      {profileOpen && !profile?.email && (
        <div className="mt-4">
          <Text html={profileAction?.benefits_title} className="text-sm font-bold leading-[1.4]" />
          <div className="mt-2 flex flex-col gap-1">
            {profileAction?.benefits?.map((item: any) => (
              <div key={item.id} className="flex items-center gap-[6px]">
                <Picture source={item.benefitIcon?.url} className="size-4" alt={item.benefit} width={16} height={16} />
                <Text html={item.benefit} className="text-sm font-bold leading-[1.4]" />
              </div>
            ))}
          </div>
        </div>
      )}
      {!profile?.email && (
        <div className="mt-4 flex items-center gap-3">
          <Button className="tablet:flex-none flex-1" variant="secondary" size="base" onClick={() => event?.join?.()}>
            <Text html={profileAction?.primaryButton || 'Join Now'} className="text-sm font-bold leading-[1.4]" />
          </Button>
          <Button className="tablet:flex-none flex-1" variant="primary" size="base" onClick={() => event?.login?.()}>
            <Text html={profileAction?.secondaryButton || 'Log In'} className="text-sm font-bold leading-[1.4]" />
          </Button>
        </div>
      )}
    </div>
  )
}

const PrimaryHeader = ({ menuOpen, onMenuOpenClose, onMenuOpenClick, actions }: MobilePrimaryNavigationData) => {
  return (
    <div className="flex h-full items-center justify-between gap-4">
      <Logo />
      <div className="desktop:gap-6 flex items-center gap-4">
        <Actions actions={actions} />
        {menuOpen ? (
          <button
            className="cursor-pointer border-0 bg-transparent p-0"
            onClick={() => onMenuOpenClose()}
            aria-label="Close menu"
            aria-expanded="true"
          >
            <Close className="size-5" aria-hidden="true" />
          </button>
        ) : (
          <button
            className="cursor-pointer border-0 bg-transparent p-0"
            onClick={() => onMenuOpenClick()}
            aria-label="Open menu"
            aria-expanded="false"
            aria-haspopup="true"
          >
            <Menu className="size-5" aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  )
}

const Logo = () => {
  const { payloadData, isMobile } = useNavContext()
  const { locale = 'us' } = useAiuiContext()
  return (
    <a
      href={getLocalizedPath('/', locale)}
      className="hover:text-brand-0 [&>svg]:w-full"
      aria-label="Home"
      dangerouslySetInnerHTML={{
        __html: isMobile ? payloadData?.bar?.mobileLogo : payloadData?.bar?.desktopLogo,
      }}
    />
  )
}

const Actions = forwardRef<HTMLButtonElement, { actions: any; activeStatus?: boolean }>(
  ({ actions, activeStatus = false }, ref) => {
    const { event, cartCount } = useNavContext()
    const [activeAction, setActiveAction] = useState<any>(null)

    const getActionLabel = (blockType: string) => {
      switch (blockType) {
        case HeaderNavigationActionBlockType.Search:
          return 'Search'
        case HeaderNavigationActionBlockType.Cart:
          return `Shopping cart${cartCount > 0 ? ` (${cartCount} items)` : ''}`
        case HeaderNavigationActionBlockType.Profile:
          return 'User profile'
        case HeaderNavigationActionBlockType.Livestream:
          return 'Livestream'
        default:
          return 'Action'
      }
    }

    const handleActionClick = useCallback(
      (action: any, index: number) => {
        setActiveAction(index)
        switch (action?.blockType) {
          case HeaderNavigationActionBlockType.Search:
            event?.search?.()
            break
          case HeaderNavigationActionBlockType.Cart:
            event?.cart?.()
            break
          case HeaderNavigationActionBlockType.Profile:
            event?.profile?.()
            break
          case HeaderNavigationActionBlockType.Livestream:
            event?.livestream?.()
            break
          default:
            return () => {}
        }
      },
      [event]
    )

    return (
      <div className="desktop:gap-6 flex items-center gap-4">
        {Array.isArray(actions) &&
          actions?.map((action: any, index: number) => (
            <button
              className="relative cursor-pointer border-0 bg-transparent p-0"
              key={action.id}
              ref={action.blockType === HeaderNavigationActionBlockType.Profile ? ref : null}
              onClick={() => handleActionClick(action, index)}
              aria-label={getActionLabel(action.blockType)}
              aria-pressed={activeStatus && activeAction === index}
            >
              <Text
                html={action.icon}
                className={cn('size-5', { 'text-brand-0': activeStatus && activeAction === index })}
                aria-hidden="true"
              />
              {action.blockType === HeaderNavigationActionBlockType.Cart && cartCount > 0 && (
                <div
                  className="bg-brand-0 absolute right-[-12px] top-[calc(100%-16px)] z-[1] flex min-h-5 min-w-5 items-center justify-center rounded-full px-[2px]"
                  aria-hidden="true"
                >
                  <Text html={cartCount?.toString()} className="text-sm font-bold leading-[1.2] text-white" />
                </div>
              )}
            </button>
          ))}
      </div>
    )
  }
)

const SecondaryHeader = ({ title, onMenuOpenClose, onMenuBackClick }: MobileSecondaryNavigationData) => {
  return (
    <div className="flex h-full items-center gap-3">
      <button
        className="cursor-pointer border-0 bg-transparent p-0"
        onClick={() => onMenuBackClick()}
        aria-label="Back to previous menu"
      >
        <LeftArrow className="size-5" aria-hidden="true" />
      </button>
      <Text html={title} className="flex-1 text-center text-base font-bold leading-[1.4]" />
      <button
        className="cursor-pointer border-0 bg-transparent p-0"
        onClick={() => onMenuOpenClose()}
        aria-label="Close menu"
      >
        <Close className="size-5" aria-hidden="true" />
      </button>
    </div>
  )
}

MobileNavigation.displayName = 'MobileNavigation'
Actions.displayName = 'Actions'
HeaderNavigation.displayName = 'HeaderNavigation'
DesktopNavigation.displayName = 'DesktopNavigation'

export default withLayout(HeaderNavigation)
